<script setup lang="ts">
import type { ValidData as KpiReportGenerateDialogFormValidData } from '@/components/dialogForms/KpiReportGenerateDialogForm.vue';
import type { DialogName } from '@/stores/useDialogStore';

import TableFooterBar from '@/components/bars/TableFooterBar.vue';
import KBtn from '@/components/btns/KBtn.vue';
import TablePageFooter from '@/components/footers/TablePageFooter.vue';
import ExportAsCsvIconBtn from '@/components/iconBtns/ExportAsCsvIconBtn.vue';
import KpiReportSearchForm from '@/components/searchForms/KpiReportSearchForm.vue';
import KpiReportTable from '@/components/tables/KpiReportTable.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import TablePageShadowWrapper from '@/components/wrappers/TablePageShadowWrapper.vue';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import useDownloadCsv from '@/composables/useDownloadCsv';
import useKpiReportView from '@/composables/views/useKpiReportView';
import { useDialogStore } from '@/stores/useDialogStore';
import { fetchPostReportsGenerateKpiReport } from '@/helpers/fetchers';
import { defCurPage, defPageSize } from '@/constants/routeQuery';

const { queryCurPage, queryPageSize, queryCustomer } = useCommonRouteQuery({
  queryCurPage: defCurPage,
  queryPageSize: defPageSize,
});
const { isDownloadingCSV, downloadCsv } = useDownloadCsv();
const {
  search,
  initialData,
  isFetchingData,
  total,
  kpiReportResults,
  resultsForCsv,
} = useKpiReportView(queryCurPage, queryPageSize, queryCustomer);

const dialogStore = useDialogStore();

function openDialog() {
  const dialogName: DialogName = 'kpiReportGenerate';
  dialogStore.open(dialogName, {
    confirm: async (formData: KpiReportGenerateDialogFormValidData) => {
      const customer = queryCustomer.value;
      if (customer === undefined) return;
      return fetchPostReportsGenerateKpiReport({
        date: formData.date,
        customer,
      });
    },
    customer: queryCustomer.value,
  });
}
</script>

<template>
  <div :class="$style.kpiReportView" data-testid="kpiReportView">
    <PageContentWrapper>
      <FormWrapper :class="$style.formWrapper">
        <h2 class="g-h2">
          To list reports and create new report choose a required customer
        </h2>
        <KpiReportSearchForm
          :initial-data="initialData"
          :loading="isFetchingData"
          :class="$style.form"
          @submit="search"
        />
      </FormWrapper>
      <TablePageShadowWrapper :class="$style.tablePageShadowWrapper">
        <KpiReportTable
          :loading="isFetchingData"
          :data="kpiReportResults || []"
        />
        <TablePageFooter
          v-if="kpiReportResults"
          :cur-page="queryCurPage"
          :page-size="queryPageSize"
          :total="total"
        >
          <ExportAsCsvIconBtn
            :disabled="isFetchingData || !kpiReportResults?.length"
            :loading="isDownloadingCSV"
            @click="downloadCsv(resultsForCsv, 'kpiReport')"
          />
        </TablePageFooter>
      </TablePageShadowWrapper>
    </PageContentWrapper>
    <TableFooterBar>
      <KBtn
        size="lg"
        append-icon="mdi-send"
        :disabled="isFetchingData || !kpiReportResults?.length"
        @click="openDialog"
      >
        Generate new report
      </KBtn>
    </TableFooterBar>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.kpiReportView {
}
  .formWrapper {
    padding: 0.5rem 1.5rem 0.5rem 1.5rem;
    max-width: variables.$tableWrapperWidth;
    margin: 2rem 0;
  }
  .tablePageShadowWrapper {
    max-width: variables.$tableWrapperWidth;
  }
</style>
